import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Briefcase, Mail, Phone, Plus } from 'lucide-react'
import { useClient } from '@/lib/queries.js'
import { fullName } from '@/lib/utils.js'
import { formatDate, money, timeAgo } from '@/lib/format.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle, DetailRow, PageHeader } from '@/components/ui/card.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { NewJobDialog } from './NewJobDialog.jsx'
import { humanMessage } from '@/lib/errors.js'

/**
 * Client detail (Phase 1b screen 4).
 *
 * One person, and every job they have ever asked about. That second part is
 * the point: Phase 1 D2 merged leads into clients precisely so that a repeat
 * enquiry from the same homeowner becomes a second job rather than a second
 * person, and this screen is where that decision pays off — the history is
 * visible instead of scattered across duplicate records.
 *
 * Email and phone are real links. On a phone, `tel:` is the difference between
 * a contractor calling a client in one tap and copying digits by hand.
 */
export function ClientDetail () {
  const { id } = useParams()
  const { data, isPending, error } = useClient(id)
  const [creatingJob, setCreatingJob] = React.useState(false)

  if (isPending) {
    return <div className="flex justify-center py-16"><Spinner label="Loading client" /></div>
  }

  if (error) {
    return (
      <>
        <PageHeader back={<BackLink />} title="Could not load this client" />
        <Alert tone="danger">{humanMessage(error)}</Alert>
      </>
    )
  }

  const { client, jobs = [] } = data
  const name = fullName(client)

  return (
    <>
      <PageHeader
        back={<BackLink />}
        title={name}
        description={client.do_not_contact
          ? <span className="text-danger-ink">Marked do not contact</span>
          : `Client since ${formatDate(client.created_at)}`}
        actions={<Button onClick={() => setCreatingJob(true)}><Plus /> New job</Button>}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Jobs</CardTitle>
              <span className="text-xs text-ink-muted">{jobs.length}</span>
            </CardHeader>
            <CardBody className="p-0">
              {jobs.length === 0
                ? (
                  <div className="px-4 py-6 sm:px-5">
                    <EmptyState
                      icon={Briefcase}
                      title="No jobs yet"
                      description={`Nothing has been quoted or scheduled for ${name} so far.`}
                      action={{ label: 'Create a job', onClick: () => setCreatingJob(true) }}
                      className="border-0 bg-transparent py-0"
                    />
                  </div>
                  )
                : (
                  <ul className="divide-y divide-line">
                    {jobs.map(job => (
                      <li key={job.id}>
                        <Link
                          to={`/jobs/${job.id}`}
                          className="flex min-h-tap items-center gap-3 px-4 py-3 hover:bg-surface-sunken sm:px-5"
                          data-testid="client-job-row"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-ink">{job.title}</span>
                            <span className="tabular block font-mono text-xs text-ink-subtle">
                              {job.job_number} · {timeAgo(job.created_at)}
                            </span>
                          </span>
                          <StatusBadge status={job.status} />
                          {Number(job.estimated_value) > 0 && (
                            <span className="tabular hidden text-sm text-ink sm:block">
                              {money(job.estimated_value)}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
            <CardBody className="space-y-3">
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="flex min-h-tap items-center gap-2.5 text-sm text-brand hover:underline"
                >
                  <Mail className="size-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{client.email}</span>
                </a>
              )}
              {client.phone && (
                <a
                  href={`tel:${client.phone.replace(/[^\d+]/g, '')}`}
                  className="flex min-h-tap items-center gap-2.5 text-sm text-brand hover:underline"
                >
                  <Phone className="size-4 shrink-0" aria-hidden="true" />
                  {client.phone}
                </a>
              )}
              {!client.email && !client.phone && (
                <p className="text-sm text-ink-muted">No contact details on file.</p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Address</CardTitle></CardHeader>
            <CardBody>
              <dl className="space-y-2.5">
                <DetailRow label="Billing">
                  {formatAddress(client.address, client.city, client.state, client.zip)}
                </DetailRow>
                <DetailRow label="Property">
                  {formatAddress(
                    client.property_address, client.property_city,
                    client.property_state, client.property_zip
                  )}
                </DetailRow>
              </dl>
            </CardBody>
          </Card>

          {client.notes && (
            <Card>
              <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-ink">{client.notes}</p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      <NewJobDialog open={creatingJob} onOpenChange={setCreatingJob} clientId={client.id} />
    </>
  )
}

/** Contracting keeps billing and property addresses separate; both may be blank. */
function formatAddress (line, city, state, zip) {
  const parts = [line, [city, state].filter(Boolean).join(', '), zip].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

function BackLink () {
  return (
    <Link to="/clients" className="mb-2 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink">
      <ArrowLeft className="size-4" aria-hidden="true" /> Clients
    </Link>
  )
}
